<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { object, array, date, string } from 'yup'

import {
  createTransferRequest,
  getPayrollInfo,
  getPayrollsDestination,
  getInfoEmployeeById,
  getInfoEmployees,
  getReasons,
  getPositions,
  getJourneys,
  getTurns,
  getSucursals,
  getDirections,
  getManagements,
  getDepartments,
  getCostCenters,
  checkEmployeeCodeExists
} from '@/app/core/services/transfer-requests'
import {
  DCard,
  DBtn,
  DSelectFieldSearch,
  DLoading,
  DTextAreaField,
  DIcon,
  DTextField,
  DModal
} from 'front-library'
usePayrollChange(() => window.location.reload())

/* router, notifications */
const router = useRouter()
const notifications = useNotificationsStore()
const { t } = useI18n()


/* Datos Listas */
const countryDestinationId = ref<string>('')
const companyDestinationId = ref<string>('')
const payrollInfo = ref<any>({})
const payrollsDestination = ref<any[]>([])
const employeeInfo = ref<any>({})
const employeeDestinationInfo = ref<any>({})
const employeesOrigin = ref<any[]>([])
const reasons = ref<any[]>([])
const positions = ref<any[]>([])
const journeys = ref<any[]>([])
const turns = ref<any[]>([])
const sucursals = ref<any[]>([])
const directions = ref<any[]>([])
const managements = ref<any[]>([])
const departments = ref<any[]>([])
const costCenters = ref<any[]>([])
const searchDestinations = ref<string>('')
const searchEmployees = ref<string>('')
const searchPositions = ref<string>('')
const searchTurns = ref<string>('')
const searchJourneys = ref<string>('')
const searchReasons = ref<string>('')
const searchManagements = ref<string>('')
const searchDepartments = ref<string>('')
const searchCostCenters = ref<string>('')
const searchSucursals = ref<string>('')
const searchDirections = ref<string>('')

/* Datos componente DLoading */
const loading = ref(false)
const messageLoading = ref('')
const showModal = ref(false)
const descriptionModal = ref('')

/* Propiedades Computadas */
const payrollDestination = computed(() => {
  return payrollsDestination.value.find(
    (payroll) => payroll.id === payrollDestinationId.value
  )
})
const filterOptions = computed(() => {
  return (items: any, search: any) =>
  (items = items.filter(
    (item: any) =>
      item.name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .indexOf(
          search
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
        ) > -1
  ))
})

/* Definicion de  campos del formulario */
const { value: payrollDestinationId } = useField<string>(ref('payrollDestinationId'))
const { value: employeeOriginId } = useField<string>(ref('employeeOriginId'))

const validationSchema = object().shape({
})

const getEmployee = async (employeeId: string) => {
  loading.value = true
  messageLoading.value = t('transfer.notification.loading')
  try {
    employeeInfo.value = await getInfoEmployeeById(employeeId)
    if (employeeInfo.value === undefined) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('transfer.notification.error'),
        type: 'error',
        time: 10000
      })
    }
  } catch (error: any) {
    notificationError(error, 'transfer', t, notifications, loading)
  } finally {
    loading.value = false
  }
}

const getEmployees = async () => {
  loading.value = true
  messageLoading.value = t('transfer.notification.loading')
  try {
    countryDestinationId.value = payrollDestination.value.countryId
    companyDestinationId.value = payrollDestination.value.companyId
    employeesOrigin.value = await getInfoEmployees()
    if (employeesOrigin.value.length === 0) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('transfer.notification.error'),
        type: 'error',
        time: 10000
      })
    }
  } catch (error: any) {
    notificationError(error, 'transfer', t, notifications, loading)
  } finally {
    loading.value = false
  }
}

const getInfoDestination = async () => {
  loading.value = true
  messageLoading.value = t('transfer.notification.loading')
  try {
    const [promiseReasons, promisePositions, promiseJourneys, promiseSucursals, promiseDirections, promiseCostCenters] = await Promise.all([
      getReasons(countryDestinationId.value),
      getPositions(countryDestinationId.value),
      getJourneys(countryDestinationId.value),
      getSucursals(companyDestinationId.value),
      getDirections(companyDestinationId.value),
      getCostCenters(companyDestinationId.value)
    ])
    reasons.value = promiseReasons
    positions.value = promisePositions
    journeys.value = promiseJourneys
    sucursals.value = promiseSucursals
    directions.value = promiseDirections
    costCenters.value = promiseCostCenters

    if (reasons.value.length === 0 || positions.value.length === 0 ||
      sucursals.value.length === 0 || directions.value.length === 0 || costCenters.value.length === 0) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('transfer.notification.error'),
        type: 'error',
        time: 10000
      })
    }
  } catch (error: any) {
    notificationError(error, 'transfer', t, notifications, loading)
  } finally {
    loading.value = false
  }
}

const getInfoManagement = async (directionId: string) => {
  loading.value = true
  messageLoading.value = t('transfer.notification.loading')
  try {
    managements.value = await getManagements(directionId)
    if (managements.value.length === 0) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('transfer.notification.errorManagement'),
        type: 'error',
        time: 10000
      })
    }
  } catch (error: any) {
    notificationError(error, 'transfer', t, notifications, loading)
  } finally {
    loading.value = false
  }
}

const getInfoDepartments = async (managementId: string, costCenterId: string) => {
  loading.value = true
  messageLoading.value = t('transfer.notification.loading')
  try {
    departments.value = await getDepartments(managementId, costCenterId)
    if (departments.value.length === 0) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('transfer.notification.errorDepartment'),
        type: 'error',
        time: 10000
      })
    }
  } catch (error: any) {
    notificationError(error, 'transfer', t, notifications, loading)
  } finally {
    loading.value = false
  }
}

const getInfoTurn = async (sucursalId: string) => {
  loading.value = true
  messageLoading.value = t('transfer.notification.loading')
  try {
    turns.value = await getTurns(sucursalId)
    if (turns.value.length === 0) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('transfer.notification.errorTurn'),
        type: 'error',
        time: 10000
      })
    }
  } catch (error: any) {
    notificationError(error, 'transfer', t, notifications, loading)
  } finally {
    loading.value = false
  }
}

const safeFormatDate = (rawValue: any) => {
  if (!rawValue) return '';
  const d = new Date(rawValue);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

const onSubmit = async (values: any) => {
  loading.value = true
  messageLoading.value = ''
  let id: any = false
  try {

    const data = {
      payrollOriginId: payrollInfo.value.id,
      payrollDestinationId: payrollDestination.value.id,
      employeeOriginId: employeeOriginId.value,
      employee: {
        code: values.code,
        leaveDate: values.leaveDate,
        leaveReasonId: values.leaveReason,
        positionId: values.position,
        journeyId: values.journey,
        sucursalId: values.sucursal,
        turnId: values.turn,
        directionId: values.direction,
        managementId: values.management,
        costCenterId: values.costCenter,
        departmentId: values.department
      },
      observations: values.observations
    }
    id = await createTransferRequest(data)
    if (id) {
      loading.value = false
      notifications.addNotification({
        title: t('notification.success'),
        text: t('transfer.notification.success'),
        type: 'success',
        time: 5000
      })
      void router.push({ name: 'ListTransferRequests' })
    }
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
  }
}

const openModal = async (values: any) => {
  const employeeCodeExists = await checkEmployeeCodeExists(
    payrollDestination.value.id,
    values.code
  )

  if (employeeCodeExists) {
    notifications.addNotification({
      title: t('notification.error'),
      text: t('transfer.notification.employeeCodeExists'),
      type: 'error',
      time: 10000
    })
    loading.value = false
    return
  }

  showModal.value = true
}

const back = () => {
  void router.push({ name: 'ListTransferRequests' })
}

onMounted(async () => {
  try {
    payrollInfo.value = await getPayrollInfo()
    payrollsDestination.value = await getPayrollsDestination(payrollInfo.value.countryId)
    if (payrollInfo.value === undefined) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('transfer.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    }
  } catch (error: any) {
    notificationError(error, 'transfer', t, notifications, loading)
  }
})

</script>
<template>
  <div class="h-full px-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal :open="showModal" :description="descriptionModal" :isFooter="false"
      :size="'w-1/4 md:w-2/5 lg:w-1/3 xl:w-1/4 2xl:w-1/4'">
      <template v-slot:form>
        <div class="flex flex-col items-center gap-10">
          <section class="px-5 text-center text-gray-400 text-sm">
            <h1 class="text-black text-xl" v-html="t('transfer.confirmationTitle')" />
            <p class="mt-3">
              {{ t('transfer.createView.confirmation') }}
            </p>
            <DTextAreaField v-model="employeeDestinationInfo['observations']" :placeholder="t('transfer.reason')"
              :rows="7" />
          </section>
          <section class="flex gap-3">
            <DBtn color="secondary" class="mr-3" @click="showModal = false" v-html="t('form.button.cancel')" />
            <DBtn @click="onSubmit(employeeDestinationInfo)" v-html="t('transfer.confirmation')" />
          </section>
        </div>
      </template>
    </DModal>
    <div class="bg-gray-50 pb-10">
      <div class="leading-4 pt-responsive">
        <p class="text-3xl font-semibold text-shadow text-blue-900" v-html="t('transfer.create')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 12rem)">
      <DCard>
        <div class="grid grid-cols-2 gap-4">
          <div class="h-[300px] bg-gray-100 border border-gray-250 rounded">
            <h1 class="m-4">{{ t('transfer.list.payrollOrigin') }}</h1>
            <div class="m-8">
              <DIcon :name="payrollInfo?.isoCode?.toUpperCase() + 'Icon'" class="h-10 w-10" />
            </div>
            <ul class="m-8">
              <li>{{ payrollInfo['code'] }} - {{ payrollInfo['name'] }}</li>
              <li>{{ payrollInfo['frequency'] }}</li>
              <li>{{ payrollInfo['currencySymbol'] }} - {{ payrollInfo['currency'] }}</li>
              <li>{{ t('transfer.createView.lastPayment') }} : {{ new
                Date(payrollInfo['lastPayment']).toLocaleDateString() }}</li>
              <li>{{ t('transfer.createView.company') }} : {{ payrollInfo['companyCode'] }} - {{
                payrollInfo['companyName'] }} </li>
            </ul>

          </div>
          <div class="h-[300px] bg-gray-100 border border-gray-250 rounded">
            <h1 class="m-4">{{ t('transfer.list.payrollDestination') }}</h1>
            <DSelectFieldSearch class="m-5" v-model="payrollDestinationId"
              :options="filterOptions(payrollsDestination, searchDestinations)" :required="true"
              @change="getEmployees()" @filter="searchDestinations = $event" />
            <div v-if="payrollDestination">
              <ul class="m-8">
                <li>{{ payrollDestination?.['name'] }}</li>
                <li>{{ payrollDestination?.['frequency'] }}</li>
                <li>{{ payrollDestination?.['currencySymbol'] }} - {{ payrollDestination?.['currency'] }}</li>
                <li>{{ t('transfer.createView.lastPayment') }} : {{ new
                  Date(payrollDestination?.['lastPayment']).toLocaleDateString() }}</li>
                <li>{{ t('transfer.createView.company') }} : {{ payrollDestination?.['companyCode'] }} - {{
                  payrollDestination?.['companyName'] }} </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="row-span-1 bg-gray-100 border border-gray-250 rounded p-4">
          <h1 class="m-4">{{ t('transfer.createView.employee') }}</h1>
          <DSelectFieldSearch class="m-4" v-model="employeeOriginId"
            :options="filterOptions(employeesOrigin, searchEmployees)" :required="true"
            @change="getEmployee(employeeOriginId); getInfoDestination()" @filter="searchEmployees = $event" />
          <div v-if="employeeInfo">
            <div class="grid grid-cols-3 gap-4">
              <div class="col-span-1 p-4">
                <ul class="space-y-3">
                  <li><strong>{{ t('transfer.createView.position') }}</strong> : {{ employeeInfo['position'] }}</li>
                  <li><strong>{{ t('transfer.createView.turn') }}</strong> : {{ employeeInfo['turn'] }}</li>
                  <li><strong>{{ t('transfer.createView.joiningDate') }}</strong> :{{
                    safeFormatDate(employeeInfo['joiningDate']) }}</li>
                </ul>
              </div>
              <div class="col-span-1 p-4">
                <ul class="space-y-3">
                  <li><strong>{{ t('transfer.createView.sucursal') }}</strong> : {{ employeeInfo['sucursal'] }}</li>
                  <li><strong>{{ t('transfer.createView.turn') }}</strong> : {{ employeeInfo['turn'] }}</li>
                </ul>
              </div>
              <div class="col-span-1 p-4">
                <ul class="space-y-3">
                  <li><strong>{{ t('transfer.createView.management') }}</strong> : {{ employeeInfo['management'] }}</li>
                  <li><strong>{{ t('transfer.createView.direction') }}</strong> : {{ employeeInfo['direction'] }}</li>
                  <li><strong>{{ t('transfer.createView.department') }}</strong> : {{ employeeInfo['department'] }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="row-span-1 bg-gray-100 border border-gray-250 rounded p-4">
          <h1 class="m-4">{{ t('transfer.createView.egress') }}</h1>
          <div class="flex flex-col gap-4">
            <div class="grid grid-cols-3 gap-4">
              <div class="col-span-1 p-4">
                <DTextField type="date" :label="t('transfer.createView.leaveDate')"
                  v-model="employeeDestinationInfo['leaveDate']" :required="true" />
              </div>
              <div class="col-span-1 p-4">
                <DSelectFieldSearch v-model="employeeDestinationInfo['leaveReason']"
                  :label="t('transfer.createView.leaveReason')" :options="filterOptions(reasons, searchReasons)"
                  :required="true" @filter="searchReasons = $event" />
              </div>
            </div>
          </div>
          <h1 class="m-4">{{ t('transfer.createView.ingress') }}</h1>
          <div class="flex flex-col gap-4">
            <div class="grid grid-cols-3 gap-4">
              <div class="col-span-1 p-4">
                <DTextField type="text" :label="t('transfer.createView.code')" v-model="employeeDestinationInfo['code']"
                  :required="true" />
              </div>
              <div class="col-span-1 p-4">
                <DSelectFieldSearch v-model="employeeDestinationInfo['position']"
                  :label="t('transfer.createView.position')" :options="filterOptions(positions, searchPositions)"
                  :required="true" @filter="searchPositions = $event" />
              </div>
              <div class="col-span-1 p-4">
                <DSelectFieldSearch v-model="employeeDestinationInfo['journey']"
                  :label="t('transfer.createView.journey')" :options="filterOptions(journeys, searchJourneys)"
                  :required="true" @filter="searchJourneys = $event" />
              </div>
              <div class="col-span-1 p-4">
                <DSelectFieldSearch v-model="employeeDestinationInfo['sucursal']"
                  :label="t('transfer.createView.sucursal')" :options="filterOptions(sucursals, searchSucursals)"
                  :required="true" @filter="searchSucursals = $event"
                  @change="getInfoTurn(employeeDestinationInfo['sucursal'])" />
              </div>
              <div class="col-span-1 p-4">
                <DSelectFieldSearch v-model="employeeDestinationInfo['turn']" :label="t('transfer.createView.turn')"
                  :options="filterOptions(turns, searchTurns)" :required="true" @filter="searchTurns = $event" />
              </div>
              <div class="col-span-1 p-4">
                <DSelectFieldSearch v-model="employeeDestinationInfo['direction']"
                  :label="t('transfer.createView.direction')" :options="filterOptions(directions, searchDirections)"
                  :required="true" @filter="searchDirections = $event"
                  @change="getInfoManagement(employeeDestinationInfo['direction'])" />
              </div>
              <div class="col-span-1 p-4">
                <DSelectFieldSearch v-model="employeeDestinationInfo['management']"
                  :label="t('transfer.createView.management')" :options="filterOptions(managements, searchManagements)"
                  :required="true" @filter="searchManagements = $event" />
              </div>
              <div class="col-span-1 p-4">
                <DSelectFieldSearch v-model="employeeDestinationInfo['costCenter']"
                  :label="t('transfer.createView.costCenter')" :options="filterOptions(costCenters, searchCostCenters)"
                  :required="true" @filter="searchCostCenters = $event"
                  @change="getInfoDepartments(employeeDestinationInfo['management'], employeeDestinationInfo['costCenter'])" />
              </div>
              <div class="col-span-1 p-4">
                <DSelectFieldSearch v-model="employeeDestinationInfo['department']"
                  :label="t('transfer.createView.department')" :options="filterOptions(departments, searchDepartments)"
                  :required="true" @filter="searchDepartments = $event" />
              </div>
            </div>
          </div>
        </div>
      </DCard>
    </form>
    <br>
    <div class="col-span-3 flex justify-end">
      <DBtn color="secondary" class="mr-3" @click="back" v-html="t('transfer.before')" />
      <DBtn @click="openModal(employeeDestinationInfo)" v-html="t('transfer.create')" />
    </div>
  </div>
</template>
<style scoped>
.window-menu {
  min-height: 100px;
}
</style>
